import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Lock, Users } from 'lucide-react';
import {
  getNotasPrivadas, setNotasPrivadas, getNotasCompartidas, setNotasCompartidas,
} from '../../lib/citasApi';
import { formatDateTime } from '../../lib/api';
import { isDoctor, isManagement, canSeeInternalNotes, normalizeRole } from '../../lib/roles';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

/** Un cuadro de notas con guardado y pie de "quién lo ve". */
function NotaBox({ patientId, icon: Icon, titulo, quienLoVe, placeholder, cargar, guardar, testid }) {
  const [texto, setTexto] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const loadedFor = useRef(null);

  useEffect(() => {
    if (!patientId || loadedFor.current === patientId) return;
    loadedFor.current = patientId;
    setLoading(true);
    cargar(patientId)
      .then((d) => {
        setTexto(d?.texto || '');
        setMeta(d?.updated_at ? d : null);
        setDirty(false);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || `No se pudieron cargar las ${titulo.toLowerCase()}`))
      .finally(() => setLoading(false));
  }, [patientId, cargar, titulo]);

  const save = async () => {
    setSaving(true);
    try {
      const d = await guardar(patientId, texto);
      setMeta(d?.updated_at ? d : null);
      setDirty(false);
      toast.success(`${titulo} guardadas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#13231E]" /> {titulo}
        </span>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : (
        <>
          <Textarea
            value={texto}
            onChange={(e) => { setTexto(e.target.value); setDirty(true); }}
            placeholder={placeholder}
            rows={3}
            className="font-mono text-xs min-h-0"
            data-testid={`${testid}-texto`}
          />
          {/* Debajo del cuadro: quién lo ve, en claro (y Guardar al lado) */}
          <div className="mt-1.5 flex items-center justify-between gap-2">
            {/* El mismo icono del título (candado o personas), no un emoji:
                en los equipos de la clínica salían cuadrados (5/10/2026). */}
            <p className="flex-1 flex items-center gap-1 text-[10px] leading-tight font-medium text-[#13231E] bg-[rgba(46,110,94,0.08)] border border-[#2E6E5E]/25 rounded px-1.5 py-1">
              <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
              <span>{quienLoVe}</span>
            </p>
            <Button
              type="button"
              size="sm"
              onClick={save}
              disabled={saving || !dirty}
              className="h-7 px-2.5 text-xs bg-[#2E6E5E] hover:bg-[#1E4A3F] shrink-0"
              data-testid={`${testid}-guardar`}
            >
              {saving && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
              Guardar
            </Button>
          </div>
          {meta?.updated_at && (
            <span className="block mt-1 text-[10px] text-muted-foreground">
              Última edición: {formatDateTime(meta.updated_at)}{meta.updated_by_name ? ` · ${meta.updated_by_name}` : ''}
            </span>
          )}
        </>
      )}
    </SectionCard>
  );
}

/**
 * Notas clínicas del paciente (Phase 27.20), dentro de su historia clínica:
 *  - Privadas: solo su autor (cada doctor tiene las suyas; nadie más las ve).
 *  - Compartidas: las ven todos los doctores y administración.
 */
export default function NotasClinicas({ patient, user }) {
  // Privadas: doctores (y los administradores que también pasan consulta, que
  // tiene agenda propia). Compartidas: doctores + administración + enfermería.
  const verPrivadas = isDoctor(user) || !!user?.agenda_id;
  const verCompartidas = isDoctor(user) || isManagement(user) || canSeeInternalNotes(user)
    || normalizeRole(user?.role) === 'enfermeria';
  if (!verPrivadas && !verCompartidas) return null;

  return (
    <div className="grid grid-cols-1 gap-4">
      {verPrivadas && (
        <NotaBox
          patientId={patient?.id}
          icon={Lock}
          titulo="Notas privadas"
          quienLoVe={`Solo las ves tú (${user?.name || 'tu usuario'}). Ni otros doctores ni administración pueden leerlas.`}
          placeholder={'Tus apuntes personales sobre este paciente…'}
          cargar={getNotasPrivadas}
          guardar={setNotasPrivadas}
          testid="notas-privadas"
        />
      )}
      {verCompartidas && (
        <NotaBox
          patientId={patient?.id}
          icon={Users}
          titulo="Notas compartidas"
          quienLoVe="Las ven todos los doctores, administración y enfermería."
          placeholder={'Información clínica útil para el resto del equipo…'}
          cargar={getNotasCompartidas}
          guardar={setNotasCompartidas}
          testid="notas-compartidas"
        />
      )}
    </div>
  );
}
